import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getRequest } from '../../lib/api';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

const pulseAnimation = `
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error, already_verified
  const [message, setMessage] = useState('');
  const [customerData, setCustomerData] = useState(null);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link');
        return;
      }

      try {
        const response = await getRequest(`public/verifyCustomerEmail?token=${token}`);

        if (response?.status === 200) {
          if (response.data?.verified && response.message?.includes('already')) {
            setStatus('already_verified');
            setMessage('Your email has already been verified!');
          } else {
            setStatus('success');
            setMessage('Email verified successfully!');
          }
          setCustomerData(response.data);
        } else {
          setStatus('error');
          setMessage(response?.message || 'Verification failed');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setMessage(error.response?.data?.message || 'Invalid or expired verification token');
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <>
      <style>{pulseAnimation}</style>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: 3
        }}
      >
      <Paper
        elevation={6}
        sx={{
          maxWidth: 500,
          width: '100%',
          p: 5,
          textAlign: 'center',
          borderRadius: 3
        }}
      >
        {status === 'loading' && (
          <>
            <CircularProgress sx={{ color: '#6f42c1', mb: 3 }} size={60} />
            <Typography variant="h5" sx={{ color: '#666', mb: 2 }}>
              Verifying your email...
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Please wait while we verify your email address
            </Typography>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircleIcon
              sx={{
                fontSize: 100,
                color: '#4caf50',
                mb: 3,
                animation: 'pulse 2s infinite'
              }}
            />
            <Typography variant="h3" sx={{ color: '#4caf50', mb: 2, fontWeight: 'bold' }}>
              Thank You!
            </Typography>
            <Typography variant="h5" sx={{ color: '#333', mb: 3 }}>
              Your email has been successfully verified
            </Typography>
            {customerData && (
              <Box sx={{ mt: 3, p: 3, bgcolor: '#f0f7f4', borderRadius: 2, border: '2px solid #4caf50' }}>
                <Typography variant="body1" sx={{ color: '#333', mb: 1, fontSize: '16px' }}>
                  <strong>Customer ID:</strong> {customerData.customer_id}
                </Typography>
                <Typography variant="body1" sx={{ color: '#333', fontSize: '16px' }}>
                  <strong>Email:</strong> {customerData.email}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 4, p: 2, bgcolor: '#fff3cd', borderRadius: 2, border: '1px solid #ffc107' }}>
              <Typography variant="body1" sx={{ color: '#856404', fontWeight: 'bold' }}>
                ✓ Your account is now active!
              </Typography>
              <Typography variant="body2" sx={{ color: '#856404', mt: 1 }}>
                You can now access all features of our service
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#999', mt: 4, fontSize: '14px' }}>
              You can safely close this window now
            </Typography>
          </>
        )}

        {status === 'already_verified' && (
          <>
            <CheckCircleIcon
              sx={{
                fontSize: 100,
                color: '#2196f3',
                mb: 3
              }}
            />
            <Typography variant="h3" sx={{ color: '#2196f3', mb: 2, fontWeight: 'bold' }}>
              All Set!
            </Typography>
            <Typography variant="h5" sx={{ color: '#333', mb: 3 }}>
              Your email is already verified
            </Typography>
            {customerData && (
              <Box sx={{ mt: 3, p: 3, bgcolor: '#e3f2fd', borderRadius: 2, border: '2px solid #2196f3' }}>
                <Typography variant="body1" sx={{ color: '#333', mb: 1, fontSize: '16px' }}>
                  <strong>Customer ID:</strong> {customerData.customer_id}
                </Typography>
                <Typography variant="body1" sx={{ color: '#333', fontSize: '16px' }}>
                  <strong>Email:</strong> {customerData.email}
                </Typography>
              </Box>
            )}
            <Box sx={{ mt: 4, p: 2, bgcolor: '#d1ecf1', borderRadius: 2, border: '1px solid #17a2b8' }}>
              <Typography variant="body1" sx={{ color: '#0c5460', fontWeight: 'bold' }}>
                ✓ Your account is active
              </Typography>
              <Typography variant="body2" sx={{ color: '#0c5460', mt: 1 }}>
                No further action is required
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#999', mt: 4, fontSize: '14px' }}>
              You can safely close this window now
            </Typography>
          </>
        )}

        {status === 'error' && (
          <>
            <ErrorIcon
              sx={{
                fontSize: 80,
                color: '#f44336',
                mb: 3
              }}
            />
            <Typography variant="h4" sx={{ color: '#333', mb: 2, fontWeight: 'bold' }}>
              Verification Failed
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
              {message}
            </Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              The verification link may be invalid or expired.<br />
              Please contact support for assistance.
            </Typography>
          </>
        )}
      </Paper>
    </Box>
    </>
  );
}
